import { useRef, useState } from 'react';
import { Loader2, Sparkles, Square } from 'lucide-react';
import { agenciesWithoutAddress, autoAddContacts } from '@generated/server/findContacts';
import { getStaffPass } from '@generated/hooks/staffPass';

/** One click: for every agency with no mailing address, run Find contacts and add everyone with an email to its list. */
export function BulkFind({ onDone }: { onDone: (msg: string) => void }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ i: number; n: number; name: string } | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const stop = useRef(false);
  const [armed, setArmed] = useState(false);

  async function start() {
    setArmed(false);
    setRunning(true); setLog([]); stop.current = false;
    let people = 0, addresses = 0, failed = 0, done = 0;
    try {
      const list = (await agenciesWithoutAddress({ data: { pass: getStaffPass() } })) ?? [];
      for (const [i, a] of list.entries()) {
        if (stop.current) break;
        setProgress({ i: i + 1, n: list.length, name: a.name });
        try {
          const pass = getStaffPass();
          if (!pass) { setLog((l) => ['Your staff sign-in ended (the page was reloaded). Sign in again and press Run it for all — it picks up where it left off.', ...l]); break; }
          let r;
          try { r = await autoAddContacts({ data: { pass, id: a.id } }); }
          catch (e1) { if (e1 instanceof Error && e1.message.startsWith('CREDITS:')) throw e1; await new Promise((res) => setTimeout(res, 3000)); r = await autoAddContacts({ data: { pass, id: a.id } }); }
          people += r.added; if (r.address) addresses++; done++;
          setLog((l) => [`${a.name}: +${r.added} ${r.added === 1 ? 'person' : 'people'}${r.address ? ', address found' : ''}`, ...l]);
        } catch (e) {
          console.error(e);
          if (e instanceof Error && e.message.startsWith('CREDITS:')) { setLog((l) => [e.message.slice(9), ...l]); break; }
          if (e instanceof Error && e.message === 'Staff access only') { setLog((l) => ['Your staff sign-in ended. Sign in again and press Run it for all — it picks up where it left off.', ...l]); break; }
          failed++; setLog((l) => [`${a.name}: search failed`, ...l]);
        }
      }
      onDone(`Searched ${done} agencies: added ${people} people to email lists and found ${addresses} mailing addresses${failed ? `. ${failed} searches failed — try those with Find contacts` : ''}.`);
    } catch (e) { console.error(e); onDone('Could not start the search. Please try again.'); }
    finally { setRunning(false); setProgress(null); }
  }

  return (
    <div className="border-2 border-foreground bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold">Find contacts for every agency missing an address</p>
          <p className="text-xs text-muted-foreground">Adds everyone with an email (found or likely from the agency's pattern, plus the general inbox) and fills in the mailing address when found.</p>
        </div>
        {running
          ? <button onClick={() => { stop.current = true; }} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground px-4 text-sm font-semibold"><Square className="h-4 w-4" />Stop after this one</button>
          : armed
            ? <span className="flex flex-wrap gap-2"><button onClick={start} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-primary px-4 text-sm font-semibold text-primary-foreground"><Sparkles className="h-4 w-4" />Yes, start (about 30 sec per agency)</button><button onClick={() => setArmed(false)} className="min-h-11 border-2 border-foreground px-4 text-sm font-semibold">Cancel</button></span>
            : <button onClick={() => setArmed(true)} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-primary px-4 text-sm font-semibold text-primary-foreground"><Sparkles className="h-4 w-4" />Run it for all</button>}
      </div>
      {progress && <p className="mt-3 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />{progress.i} of {progress.n}: {progress.name}…</p>}
      {log.length > 0 && <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs">{log.map((l) => <li key={l}>{l}</li>)}</ul>}
    </div>
  );
}
